import type { EncodeRequest, EncodeResponse } from "../workers/encoder.types";
import type { QualitySettings } from "./qualitySettings";
import { trackTelemetryEvent } from "./telemetry";

const MAX_WORKERS = 8;
const ENCODE_TIMEOUT_MS = 120000;

export const DEFAULT_CONCURRENCY = Math.max(
  2,
  Math.min(6, (typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 4 : 4))
);

export const createEncoderWorker = () => {
  return new Worker(new URL("../workers/encoder.worker.ts", import.meta.url));
};

const createBackgroundRemovalWorker = () => {
  return new Worker(new URL("../workers/backgroundRemoval.worker.ts", import.meta.url));
};

interface EncoderWorkerSlot {
  worker: Worker;
  activeTaskId?: number;
}

interface EncoderTask {
  id: number;
  request: EncodeRequest;
  resolve: (response: EncodeResponse) => void;
  reject: (error: Error) => void;
  signal?: AbortSignal;
  abortHandler?: () => void;
  timeoutId?: number;
  slot?: EncoderWorkerSlot;
}

const createAbortError = () => {
  if (typeof DOMException !== "undefined") {
    return new DOMException("Encoding canceled", "AbortError");
  }
  const error = new Error("Encoding canceled");
  error.name = "AbortError";
  return error;
};

export class EncoderPool {
  private slots: EncoderWorkerSlot[] = [];
  private pending = new Map<number, EncoderTask>();
  private queue: number[] = [];
  private nextId = 0;
  private disposed = false;

  constructor(size = typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 4 : 4) {
    const workerCount = Math.max(1, Math.min(size, MAX_WORKERS));
    for (let i = 0; i < workerCount; i++) {
      this.slots.push(this.createSlot());
    }
  }

  encode(
    format: string,
    blob: Blob,
    compress: boolean,
    qualitySettings?: QualitySettings,
    signal?: AbortSignal
  ): Promise<EncodeResponse> {
    return new Promise((resolve, reject) => {
      if (this.disposed || this.slots.length === 0) {
        reject(new Error("Encoder pool is not available"));
        return;
      }
      if (signal?.aborted) {
        reject(createAbortError());
        return;
      }

      const id = this.nextId++;
      const request: EncodeRequest = {
        id,
        format,
        blob,
        compress,
        qualitySettings,
      };
      const task: EncoderTask = { id, request, resolve, reject, signal };
      if (signal) {
        task.abortHandler = () => this.cancelTask(id);
        signal.addEventListener("abort", task.abortHandler, { once: true });
      }
      this.pending.set(id, task);
      this.queue.push(id);
      this.dispatch();
    });
  }

  terminate() {
    this.disposed = true;
    this.pending.forEach((task) => {
      this.cleanupTask(task);
      task.reject(new Error("Encoder pool terminated"));
    });
    this.pending.clear();
    this.queue = [];
    this.slots.forEach((slot) => slot.worker.terminate());
    this.slots = [];
  }

  get size() {
    return this.slots.length;
  }

  private createSlot(): EncoderWorkerSlot {
    const worker = createEncoderWorker();
    const slot: EncoderWorkerSlot = { worker };
    worker.onmessage = (event: MessageEvent<EncodeResponse>) => {
      const { id } = event.data;
      if (slot.activeTaskId !== id) return;
      const task = this.pending.get(id);
      if (!task) return;
      this.finishTask(task);
      if (event.data.error) {
        task.reject(new Error(event.data.error));
      } else {
        task.resolve(event.data);
      }
      this.dispatch();
    };
    worker.onerror = (event) => {
      trackTelemetryEvent("cc_worker_error", {
        worker_type: "encoder",
        failure_type: "worker",
      });
      this.failSlot(slot, new Error(event.message || "Worker error"));
    };
    worker.onmessageerror = () => {
      trackTelemetryEvent("cc_worker_error", {
        worker_type: "encoder",
        failure_type: "worker",
      });
      this.failSlot(slot, new Error("Worker message error"));
    };
    return slot;
  }

  private dispatch() {
    if (this.disposed) return;
    for (const slot of this.slots) {
      if (slot.activeTaskId !== undefined) continue;
      let task: EncoderTask | undefined;
      while (this.queue.length > 0 && !task) {
        const id = this.queue.shift();
        if (id !== undefined) task = this.pending.get(id);
      }
      if (!task) return;

      slot.activeTaskId = task.id;
      task.slot = slot;
      task.timeoutId = window.setTimeout(() => {
        trackTelemetryEvent("cc_worker_error", {
          worker_type: "encoder",
          failure_type: "timeout",
        });
        this.failSlot(slot, new Error("Encoding timed out"));
      }, ENCODE_TIMEOUT_MS);

      try {
        slot.worker.postMessage(task.request);
      } catch (error) {
        this.failSlot(slot, error instanceof Error ? error : new Error("Worker message failed"));
      }
    }
  }

  private cancelTask(id: number) {
    const task = this.pending.get(id);
    if (!task) return;
    const slot = task.slot;
    this.finishTask(task);
    task.reject(createAbortError());
    if (slot) {
      this.replaceWorker(slot);
    }
    this.dispatch();
  }

  private failSlot(slot: EncoderWorkerSlot, error: Error) {
    const task = slot.activeTaskId === undefined ? undefined : this.pending.get(slot.activeTaskId);
    if (task) {
      this.finishTask(task);
      task.reject(error);
    }
    this.replaceWorker(slot);
    this.dispatch();
  }

  private finishTask(task: EncoderTask) {
    this.pending.delete(task.id);
    this.cleanupTask(task);
    if (task.slot?.activeTaskId === task.id) {
      task.slot.activeTaskId = undefined;
    }
    task.slot = undefined;
  }

  private cleanupTask(task: EncoderTask) {
    if (task.timeoutId !== undefined) {
      window.clearTimeout(task.timeoutId);
    }
    if (task.signal && task.abortHandler) {
      task.signal.removeEventListener("abort", task.abortHandler);
    }
  }

  private replaceWorker(slot: EncoderWorkerSlot) {
    const index = this.slots.indexOf(slot);
    try {
      slot.worker.terminate();
    } catch {
      // A worker may already be terminated after a message failure.
    }
    slot.activeTaskId = undefined;
    if (this.disposed || index === -1) return;
    this.slots[index] = this.createSlot();
  }
}

let encoderPool: EncoderPool | null = null;
let backgroundWorker: Worker | null = null;
const backgroundPending = new Map<number, { resolve: (r: Blob) => void; reject: (e: Error) => void }>();
let backgroundNextId = 0;
let zipWorker: Worker | null = null;
const zipPending = new Map<number, { resolve: (r: ArrayBuffer) => void; reject: (e: Error) => void }>();
let zipNextId = 0;

export const getEncoderPool = () => {
  if (!encoderPool) {
    encoderPool = new EncoderPool();
  }
  return encoderPool;
};

export const getAdaptiveConversionConcurrency = () => {
  const cores = typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 4 : 4;
  const deviceMemory = typeof navigator !== "undefined" && "deviceMemory" in navigator
    ? Number((navigator as Navigator & { deviceMemory?: number }).deviceMemory)
    : undefined;
  const isNarrowViewport = typeof window !== "undefined" && window.innerWidth <= 768;
  const memoryCap = Number.isFinite(deviceMemory) && deviceMemory <= 4 ? 2 : DEFAULT_CONCURRENCY;
  const viewportCap = isNarrowViewport ? 2 : DEFAULT_CONCURRENCY;
  const coreCap = Math.max(1, Math.min(cores, DEFAULT_CONCURRENCY));
  const poolSize = getEncoderPool().size || DEFAULT_CONCURRENCY;
  const caps = [coreCap, memoryCap, viewportCap, poolSize].filter(Number.isFinite);
  return Math.max(1, Math.min(...caps));
};

const getBackgroundWorker = () => {
  if (!backgroundWorker) {
    const worker = createBackgroundRemovalWorker();
    worker.onmessage = (event: MessageEvent<{ id: number; blob?: Blob; error?: string }>) => {
      const { id, blob, error } = event.data;
      const handler = backgroundPending.get(id);
      if (!handler) return;

      backgroundPending.delete(id);
      if (error || !blob) {
        handler.reject(new Error(error || "Background removal failed"));
      } else {
        handler.resolve(blob);
      }
    };
    const handleFailure = (err: Error) => {
      trackTelemetryEvent("cc_worker_error", {
        worker_type: "background_removal",
        failure_type: "worker",
      });
      backgroundPending.forEach((handler) => handler.reject(err));
      backgroundPending.clear();
      try {
        worker.terminate();
      } catch {
        // A failed background worker may already be terminated.
      }
      backgroundWorker = null;
    };
    worker.onerror = (event) => handleFailure(new Error(event.message || "Background worker error"));
    worker.onmessageerror = () => handleFailure(new Error("Background worker message error"));
    backgroundWorker = worker;
  }
  return backgroundWorker;
};

const getZipWorker = () => {
  if (!zipWorker) {
    const worker = new Worker(new URL("../workers/zip.worker.ts", import.meta.url));
    worker.onmessage = (event: MessageEvent<{ id: number; buffer?: ArrayBuffer; error?: string }>) => {
      const { id, buffer, error } = event.data;
      const handler = zipPending.get(id);
      if (!handler) return;

      zipPending.delete(id);
      if (error || !buffer) {
        handler.reject(new Error(error || "Zip failed"));
      } else {
        handler.resolve(buffer);
      }
    };
    const handleFailure = (err: Error) => {
      trackTelemetryEvent("cc_worker_error", {
        worker_type: "zip",
        failure_type: "worker",
      });
      zipPending.forEach((handler) => handler.reject(err));
      zipPending.clear();
      try {
        worker.terminate();
      } catch {
        // A failed ZIP worker may already be terminated.
      }
      zipWorker = null;
    };
    worker.onerror = (event) => handleFailure(new Error(event.message || "Zip worker error"));
    worker.onmessageerror = () => handleFailure(new Error("Zip worker message error"));
    zipWorker = worker;
  }
  return zipWorker;
};

export const zipInWorker = (files: Array<{ name: string; data: ArrayBuffer }>): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const id = zipNextId++;
    zipPending.set(id, { resolve, reject });
    const worker = getZipWorker();
    const transferables = files.map((file) => file.data);
    worker.postMessage({ id, files }, transferables);
  });
};

export const disposeWorkers = () => {
  if (encoderPool) {
    encoderPool.terminate();
    encoderPool = null;
  }
  if (backgroundWorker) {
    try {
      backgroundWorker.terminate();
    } catch {
      // Background worker disposal is best-effort during app teardown.
    }
    backgroundWorker = null;
    backgroundPending.clear();
  }
  if (zipWorker) {
    try {
      zipWorker.terminate();
    } catch {
      // ZIP worker disposal is best-effort during app teardown.
    }
    zipWorker = null;
    zipPending.clear();
  }
};

export const removeBackgroundInWorker = (blob: Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const id = backgroundNextId++;
    backgroundPending.set(id, { resolve, reject });
    const worker = getBackgroundWorker();
    worker.postMessage({ id, blob });
  });
};
